import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableContainer from "@material-ui/core/TableContainer";
import EATableHead from "./tableComponents/EATableHead";
import EATableBody from "./tableComponents/EATableBody";
import EATablePagination from "./tableComponents/EATablePagination";
import "../styles/components/table.css";
import { ASC, DESC, sortBy } from "../utils/ArrayUtils";
import LoadingPlaceholder from "./LoadingPlaceholder";

const useStyles = makeStyles({
    root: {
        width: "100%",
    },
    container: {
        maxHeight: "100%",
    },
});

/**
 * Komponente zur Darstellung einer Tabelle mit Paginierung, falls entsprechend viele Ergebnisse angezeigt werden müssen
 * @param id
 * @param columns Ein Array von column Objekten => ColumnUtils können bei der Erstellung hilfreich sein
 * @param initialSortColumn
 * @param data Ein Array von darzustellenden Daten. Der jeweilige ObjektKey muss der id der entsprechenden Spalte entsprechen
 * @param loading boolean, welcher true sein sollte, wenn die Daten aktuell vom Backend geladen werden
 * @param style
 * @param rowsPerPage Die standardmäßig pro Seite dargestellten Einträge (per default 10)
 * @param rest
 * @returns {JSX.Element}
 * @constructor
 */
function EATable({
    id,
    columns,
    initialSortColumn,
    data,
    loading,
    style,
    rowsPerPage: initRowsPerPage = 10,
    onDoubleClick = null,
    onAltDoubleClick = null,
    ...rest
}) {
    const classes = useStyles();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(initRowsPerPage);
    const [order, setOrder] = useState(rest.order ? rest.order : ASC);
    const [visibleRows, setVisibleRows] = useState([]);

    function getInitialSortColumn() {
        if (initialSortColumn) return initialSortColumn;
        return columns.find((col) => col.label).id;
    }

    const [orderBy, setOrderBy] = useState(getInitialSortColumn());

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value));
        setPage(0);
    };

    function handleRequestSort(event, property) {
        const isAsc = orderBy === property && order === ASC;
        setOrder(isAsc ? DESC : ASC);
        setOrderBy(property);
    }

    useEffect(() => {
        if (orderBy && columns.some((col) => col.id === orderBy && col.orderValueExtractor)) {
            setVisibleRows(
                sortBy(data, orderBy, order, columns.find((col) => col.id === orderBy).orderValueExtractor).slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                )
            );
        } else {
            setVisibleRows(sortBy(data, orderBy, order).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage));
        }
    }, [data, columns, page, orderBy, order, rowsPerPage, setVisibleRows]);
    useEffect(() => {
        if (page !== 0 && page * rowsPerPage >= data.length) setPage(0);
    }, [data, page, rowsPerPage, setPage]);

    return (
        <Paper className={classes.root} style={style}>
            {loading && <LoadingPlaceholder />}
            {data && initRowsPerPage < data.length && (
                <EATablePagination
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    length={data.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                />
            )}
            <TableContainer className={classes.container}>
                <Table stickyHeader size="small" {...rest}>
                    <EATableHead id={id + "-head"} order={order} orderBy={orderBy} onRequestSort={handleRequestSort} headCells={columns} />
                    {!loading && (
                        <EATableBody
                            id={id + "-body"}
                            columns={columns}
                            data={data}
                            visibleRows={visibleRows}
                            onDoubleClick={onDoubleClick}
                            onAltDoubleClick={onAltDoubleClick}
                        />
                    )}
                </Table>
            </TableContainer>
        </Paper>
    );
}

export default EATable;
